/**
 * The nested subitems table (spec `specs/M3-S1-subitems.md` §2.5, §6).
 *
 * Three row components, not a table: an expanded parent contributes a **header row**, `n`
 * **subitem rows** and an **add-subitem row** to the *same* flat virtual list the item rows
 * live in, so 500 subitems cost exactly what 500 items cost and the virtualiser's total height
 * stays exact with no measurement pass (`layout.ts` `subitemsBlockHeight`).
 *
 * Every number is measured on `discovery/ui-reference/subitems/02-row-expanded.png`:
 *
 *   16 px of air above the header · 32 px of indent · 36 px per row
 *   nested name column 300 px **in total** (6 px bar + 32 px rail + a 262 px cell)
 *   30 px of air below · 1 px `#d0d4e4` rules, the parent grid's own line
 *
 * P-018, re-measured on the PNG (the JSON's `tree` is `null` — monday paints the connector on
 * the grid canvas, so it has no DOM to dump): the rows run to the grid's **full width**, and
 * the block is tied to its parent by a **connector** — a 1 px group-colour spine 2 px in from
 * the grid's left edge, running from the parent row's bottom through both gaps, a 6 px bar
 * down the block's own left edge (rounded 5 px at the top and bottom of the block, 50 % alpha
 * over the add row) and a 1 px tick from the spine to the bar at each subitem row's `top + 17`.
 * Without it the 16 px of air above the header reads as stray white padding, which is the bug.
 *
 * Cells are the **same** `GridCell` as an item's, so the portalled editors from round 4 work
 * here with no second editor stack (U9).
 */
import { Icon, icons } from '@elizmonday/ui';
import { memo, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react';
import { columnWidth } from '../columns/registry';
import type { CellRenderContext } from '../columns/types';
import type { BoardColumn, ItemRow } from '../data/types';
import { GridCell } from './GridCell';
import { isNameCellOpenGesture } from './name-cell-click';
import {
  SUBITEMS_ADD_ROW_ALPHA,
  SUBITEMS_BAR_WIDTH,
  SUBITEMS_GAP_BOTTOM,
  SUBITEMS_GAP_TOP,
  SUBITEMS_SPINE_OFFSET,
  SUBITEMS_SPINE_WIDTH,
  SUBITEMS_TICK_DROP,
  SUBITEMS_TICK_WIDTH,
  SUBITEM_CHECKBOX_GUTTER,
  SUBITEM_NAME_CELL_WIDTH,
  subitemsLeft,
} from './layout';
import type { CellAddress } from './selection';

interface CommonProps {
  parentItemId: string;
  groupColor: string;
  top: number;
  height: number;
  /**
   * The parent grid's own width (sticky + scrolling). monday's nested rules run all the way to
   * it — the `+` cell is followed by empty cells to the right edge, with the row borders
   * continuing `[uis:02-row-expanded]`; ours used to stop after the `+`, leaving white (P-018).
   */
  gridWidth: number;
  columns: readonly BoardColumn[];
}

/** The connector's vertical: 1 px of group colour, `SUBITEMS_SPINE_OFFSET` in from the grid. */
function Spine({ color, top, height }: { color: string; top: number; height: number }) {
  return (
    <span
      className="ez-subrow__spine"
      aria-hidden="true"
      style={{
        backgroundColor: color,
        left: -(subitemsLeft() - SUBITEMS_SPINE_OFFSET),
        width: SUBITEMS_SPINE_WIDTH,
        top,
        height,
      }}
    />
  );
}

/** The connector's horizontal: the elbow out of the spine into this row's bar. */
function Tick({ color, alpha = 1 }: { color: string; alpha?: number }) {
  return (
    <span
      className="ez-subrow__tick"
      aria-hidden="true"
      style={{
        borderColor: color,
        opacity: alpha,
        left: -SUBITEMS_TICK_WIDTH,
        width: SUBITEMS_TICK_WIDTH,
        height: SUBITEMS_TICK_DROP,
      }}
    />
  );
}

/** The row's slice of the block's left-hand bar. */
function Bar({ color, className, alpha = 1 }: { color: string; className?: string; alpha?: number }) {
  return (
    <span
      className={className === undefined ? 'ez-subrow__bar' : `ez-subrow__bar ${className}`}
      style={{ backgroundColor: color, width: SUBITEMS_BAR_WIDTH, opacity: alpha }}
      aria-hidden="true"
    />
  );
}

/** Everything right of the last column: empty, but it carries the row's rule to the edge. */
function Filler() {
  return <span className="ez-subrow__filler" aria-hidden="true" />;
}

/** The rail is measured from the block's left edge, so the bar is inside it. */
const RAIL_WIDTH = SUBITEM_CHECKBOX_GUTTER - SUBITEMS_BAR_WIDTH;

/* ── header ───────────────────────────────────────────────────────────────── */

export interface SubitemsHeaderRowProps extends CommonProps {
  /** monday titles the nested name column with the subitems board's own `name` column. */
  nameTitle: string;
  canEditStructure: boolean;
  /** P-326: `(x, y)` is under the `+`, where monday opens the gallery `[uis:20-nested-add-column]`. */
  onAddColumn(x: number, y: number): void;
  onOpenColumnMenu(columnId: string, x: number, y: number): void;
}

export const SubitemsHeaderRow = memo(function SubitemsHeaderRow({
  parentItemId,
  groupColor,
  top,
  height,
  gridWidth,
  columns,
  nameTitle,
  canEditStructure,
  onAddColumn,
  onOpenColumnMenu,
}: SubitemsHeaderRowProps) {
  const rest = columns.filter((column) => column.id !== 'name');
  return (
    <div
      className="ez-subrow ez-subrow--header"
      role="row"
      data-testid={`subitems-header-${parentItemId}`}
      data-parent-item-id={parentItemId}
      style={{ top, height, left: subitemsLeft(), width: Math.max(0, gridWidth - subitemsLeft()) }}
    >
      {/* The header carries the spine across the 16 px of air above it. */}
      <Spine color={groupColor} top={-SUBITEMS_GAP_TOP} height={SUBITEMS_GAP_TOP + height} />
      <Bar color={groupColor} className="ez-subrow__bar--top" />
      <span className="ez-subrow__gutter" style={{ width: RAIL_WIDTH }} aria-hidden="true" />
      <span
        className="ez-subrow__title"
        role="columnheader"
        style={{ width: SUBITEM_NAME_CELL_WIDTH }}
        data-testid={`subitems-header-name-${parentItemId}`}
      >
        {nameTitle}
      </span>
      {rest.map((column) => (
        <span
          key={column.id}
          className="ez-subrow__title"
          role="columnheader"
          style={{ width: columnWidth(column) }}
          data-testid={`subitems-header-${column.id}-${parentItemId}`}
          onClick={(event) => {
            const box = event.currentTarget.getBoundingClientRect();
            onOpenColumnMenu(column.id, box.right, box.bottom);
          }}
        >
          {column.title}
        </span>
      ))}
      {canEditStructure ? (
        <button
          type="button"
          className="ez-subrow__add-column"
          aria-label="Add column to the subitems board"
          title="Add column"
          data-testid={`subitems-add-column-${parentItemId}`}
          onClick={(event) => {
            const box = event.currentTarget.getBoundingClientRect();
            onAddColumn(Math.round(box.left), Math.round(box.bottom + 4));
          }}
        >
          <Icon icon={icons.add} size={14} ignoreFocusStyle />
        </button>
      ) : null}
      <Filler />
    </div>
  );
});

/* ── P-308: the parent whose subitems could not be read ───────────────────── */

export interface SubitemsUnavailableRowProps {
  parentItemId: string;
  groupColor: string;
  top: number;
  height: number;
  gridWidth: number;
  /** The api's own sentence, e.g. *"Board 6061227186 not found"* — never a rewrite of it. */
  reason: string;
}

/**
 * **P-308.** One muted line under a parent whose subitems read was refused, in place of the
 * nested block. monday has no such state — its subitems board always belongs to the board it
 * hangs off — so this is the smallest honest thing: the row stays, the board stays, and the
 * api's sentence is on the line's `title` (hover) rather than shouted in the grid. The toast
 * has already said it once; this is the reminder the row carries afterwards.
 */
export const SubitemsUnavailableRow = memo(function SubitemsUnavailableRow({
  parentItemId,
  groupColor,
  top,
  height,
  gridWidth,
  reason,
}: SubitemsUnavailableRowProps) {
  return (
    <div
      className="ez-subrow ez-subrow--unavailable"
      role="row"
      data-testid={`subitems-unavailable-${parentItemId}`}
      data-parent-item-id={parentItemId}
      style={{ top, height, left: subitemsLeft(), width: Math.max(0, gridWidth - subitemsLeft()) }}
    >
      <Bar color={groupColor} alpha={SUBITEMS_ADD_ROW_ALPHA} />
      <span className="ez-subrow__gutter" style={{ width: RAIL_WIDTH }} aria-hidden="true" />
      <span className="ez-subrow__unavailable" title={reason}>
        Subitems unavailable
      </span>
      <Filler />
    </div>
  );
});

/* ── one subitem ──────────────────────────────────────────────────────────── */

export interface SubitemRowProps extends CommonProps {
  subitem: ItemRow;
  rowIndex: number;
  context: CellRenderContext;
  editing: { address: CellAddress; initialInput?: string } | null;
  readOnlyColumnIds: readonly string[];
  onFocusCell(address: CellAddress, event: ReactMouseEvent): void;
  onStartEditing(address: CellAddress): void;
  onCommit(address: CellAddress, value: unknown): void;
  onCancelEditing(): void;
  onNavigate(direction: 'up' | 'down' | 'left' | 'right' | 'next' | 'prev'): void;
  onOpenMenu(x: number, y: number, context?: boolean): void;
  onOpenSubitem(): void;
  /**
   * **P-320.** The subitem card on its **Updates** tab — what a click beside the subitem's name
   * opens, the same rule an item row follows. Separate from `onOpenSubitem` (the open arrow,
   * which asks for whichever tab the card was left on) because the tab is part of what the
   * gesture means, exactly as it is for the item row's conversation bubble (P-266).
   */
  onOpenSubitemUpdates(): void;
}

export const SubitemRow = memo(function SubitemRow({
  parentItemId,
  subitem,
  rowIndex,
  groupColor,
  top,
  height,
  gridWidth,
  columns,
  context,
  editing,
  readOnlyColumnIds,
  onFocusCell,
  onStartEditing,
  onCommit,
  onCancelEditing,
  onNavigate,
  onOpenMenu,
  onOpenSubitem,
  onOpenSubitemUpdates,
}: SubitemRowProps) {
  const readOnly = new Set(readOnlyColumnIds);
  const nameColumn = columns.find((column) => column.id === 'name');
  const rest = columns.filter((column) => column.id !== 'name');
  /**
   * Cell focus for a subitem is **local to the row**. The grid's `GridSelection` is built from
   * `GridGeometry`, whose row list is the item rows — handing it a subitem id would make every
   * arrow key and range extension answer nonsense. What the cell actually needs from focus is
   * monday's "first click selects, second click edits" rule (`[B5§6.1]`), and that is exactly
   * what this holds; U9's editor would never open without it.
   */
  const [focusedColumnId, setFocusedColumnId] = useState<string | null>(null);

  const cellProps = (column: BoardColumn, colIndex: number) => ({
    column,
    item: subitem,
    context,
    colIndex,
    focused: focusedColumnId === column.id,
    inRange: false,
    editing:
      editing !== null &&
      editing.address.itemId === subitem.id &&
      editing.address.columnId === column.id,
    readOnly: readOnly.has(column.id),
    initialInput: editing?.initialInput,
    onFocusCell: (address: CellAddress, event: ReactMouseEvent) => {
      setFocusedColumnId(String(address.columnId));
      onFocusCell(address, event);
    },
    onStartEditing,
    onCommit,
    onCancel: onCancelEditing,
    onNavigate,
  });

  return (
    <div
      className="ez-subrow"
      role="row"
      aria-rowindex={rowIndex + 1}
      data-testid={`subitem-${subitem.id}`}
      data-parent-item-id={parentItemId}
      style={{ top, height, left: subitemsLeft(), width: Math.max(0, gridWidth - subitemsLeft()) }}
      onContextMenu={(event) => {
        event.preventDefault();
        onOpenMenu(event.clientX, event.clientY, true);
      }}
    >
      <Spine color={groupColor} top={0} height={height} />
      <Tick color={groupColor} />
      <Bar color={groupColor} />
      <span className="ez-subrow__gutter" role="gridcell" style={{ width: RAIL_WIDTH }}>
        <button
          type="button"
          className="ez-subrow__menu"
          aria-label={`More options for ${subitem.name}`}
          data-testid={`subitem-menu-${subitem.id}`}
          onClick={(event) => {
            const box = event.currentTarget.getBoundingClientRect();
            onOpenMenu(box.right, box.bottom);
          }}
        >
          <Icon icon={icons.moreActions} size={14} ignoreFocusStyle />
        </button>
      </span>
      {nameColumn ? (
        <div
          className="ez-subrow__name"
          role="presentation"
          style={{ width: SUBITEM_NAME_CELL_WIDTH }}
          /** **P-320**, the same rule as an item row's — see `name-cell-click.ts`. */
          onMouseDownCapture={(event) => {
            if (isNameCellOpenGesture(event)) event.stopPropagation();
          }}
          onClick={(event) => {
            if (!isNameCellOpenGesture(event)) return;
            event.stopPropagation();
            onOpenSubitemUpdates();
          }}
        >
          <GridCell {...cellProps(nameColumn, 1)} width={SUBITEM_NAME_CELL_WIDTH} />
          <button
            type="button"
            className="ez-subrow__open"
            aria-label={`Open ${subitem.name}`}
            title="Open subitem"
            data-testid={`subitem-open-${subitem.id}`}
            onClick={onOpenSubitem}
          >
            <Icon icon={icons.expandRight} size={13} ignoreFocusStyle />
          </button>
        </div>
      ) : null}
      {rest.map((column, index) => (
        <GridCell key={column.id} {...cellProps(column, index + 2)} width={columnWidth(column)} />
      ))}
      <Filler />
    </div>
  );
});

/* ── + Add subitem ────────────────────────────────────────────────────────── */

export interface AddSubitemRowProps {
  parentItemId: string;
  groupColor: string;
  top: number;
  height: number;
  /**
   * The parent grid's width. The add row sits at the block's own 32 px indent like every other
   * nested row and runs to the grid's right edge: its bar is at x 423-428, its rail rule at
   * x 461 and its "+ Add subitem" ink at x 487, while its bottom rule runs x 428 -> the edge
   * (measured on `[uis:02-row-expanded]` and `[uis:03-add-subitem-row]`). Round 5i read the
   * `x 391 w 300` **cell wrapper** out of the JSON and put the whole row at the parent's left
   * edge; monday paints nothing left of the indent but the 1 px spine (P-018).
   */
  gridWidth: number;
  onAdd(name: string): void;
}

export const AddSubitemRow = memo(function AddSubitemRow({
  parentItemId,
  groupColor,
  top,
  height,
  gridWidth,
  onAdd,
}: AddSubitemRowProps) {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const name = draft.trim();
    setDraft('');
    if (name.length > 0) onAdd(name);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setDraft('');
      event.currentTarget.blur();
    }
  };

  return (
    <div
      className="ez-subrow ez-subrow--add"
      role="row"
      data-testid={`add-subitem-${parentItemId}`}
      data-parent-item-id={parentItemId}
      style={{ top, height, left: subitemsLeft(), width: Math.max(0, gridWidth - subitemsLeft()) }}
    >
      {/* The add row carries the spine on down through the 30 px of air below the block. */}
      <Spine color={groupColor} top={0} height={height + SUBITEMS_GAP_BOTTOM} />
      {/* The block's bar and tick fade to 50 % over the add row `[uis:02-row-expanded]`. */}
      <Tick color={groupColor} alpha={SUBITEMS_ADD_ROW_ALPHA} />
      <Bar color={groupColor} className="ez-subrow__bar--bottom" alpha={SUBITEMS_ADD_ROW_ALPHA} />
      <span className="ez-subrow__gutter" style={{ width: RAIL_WIDTH }} aria-hidden="true" />
      <input
        className="ez-subrow__add-input"
        type="text"
        value={draft}
        placeholder="+ Add subitem"
        aria-label={`Add a subitem to item ${parentItemId}`}
        data-testid={`add-subitem-input-${parentItemId}`}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
      />
      <Filler />
    </div>
  );
});
